import { createTheme } from '@mui/material/styles';

// Create theme based on mode (light/dark)
export const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'light' 
      ? {
          // Light mode colors
          primary: {
            main: '#1976d2',
            light: '#42a5f5',
            dark: '#1565c0',
            contrastText: '#fff',
          },
          secondary: {
            main: '#9c27b0',
            light: '#ba68c8',
            dark: '#7b1fa2',
            contrastText: '#fff',
          },
          background: {
            default: '#f5f5f5',
            paper: '#ffffff',
          },
          status: {
            online: '#4caf50',
            offline: '#9e9e9e',
            warning: '#ff9800',
            error: '#f44336',
          },
          code: {
            background: 'rgba(0, 0, 0, 0.05)',
            color: 'rgba(0, 0, 0, 0.87)'
          }
        }
      : {
          // Dark mode colors
          primary: {
            main: '#90caf9',
            light: '#e3f2fd',
            dark: '#42a5f5',
            contrastText: '#000',
          },
          secondary: {
            main: '#ce93d8',
            light: '#f3e5f5',
            dark: '#ab47bc',
            contrastText: '#000',
          },
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
          status: {
            online: '#66bb6a',
            offline: '#757575',
            warning: '#ffb74d',
            error: '#ef5350',
          },
          code: {
            background: 'rgba(255, 255, 255, 0.05)',
            color: 'rgba(255, 255, 255, 0.85)'
          }
        }),
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: mode === 'light'
            ? '0 4px 12px 0 rgba(0, 0, 0, 0.05)'
            : '0 4px 12px 0 rgba(0, 0, 0, 0.2)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: mode === 'light'
              ? '0 8px 18px 0 rgba(0, 0, 0, 0.1)'
              : '0 8px 18px 0 rgba(0, 0, 0, 0.3)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: mode === 'light'
            ? 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)'
            : 'linear-gradient(45deg, #303f9f 30%, #512da8 90%)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: mode === 'light'
              ? 'rgba(0, 0, 0, 0.02)'
              : 'rgba(255, 255, 255, 0.03)',
          },
          '&:hover': {
            backgroundColor: mode === 'light'
              ? 'rgba(0, 0, 0, 0.04)'
              : 'rgba(255, 255, 255, 0.05)',
          },
          '&.Mui-selected': {
            backgroundColor: mode === 'light'
              ? 'rgba(25, 118, 210, 0.08)'
              : 'rgba(144, 202, 249, 0.08)',
            '&:hover': {
              backgroundColor: mode === 'light'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(144, 202, 249, 0.12)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: mode === 'dark' ? '#606060 #1e1e1e' : '#c1c1c1 #f5f5f5',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#606060' : '#c1c1c1',
            minHeight: 24,
            border: mode === 'dark' ? '2px solid #1e1e1e' : '2px solid #f5f5f5',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: mode === 'dark' ? '#808080' : '#a8a8a8',
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'inherit',
          backgroundColor: 'transparent',
          // This will target the css class mentioned in the error
          '&.css-1al7nwh': {
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'inherit',
            backgroundColor: mode === 'dark' ? 'rgba(30, 30, 30, 0.9)' : 'inherit',
          }
        },
      },
    },
  },
});

// Default export as light theme for backward compatibility
export default createAppTheme('light');
